import {useCallback, useMemo} from 'react'
import {useAlerts} from '../../../components/alerts/useAlerts'
import {useLoadingState} from '../../../components/hooks/useLoadingState'
import {useDispatch} from 'react-redux'
import {TicketPortalModel} from '../../../models/ems/TicketModel'
import {actions} from '../redux/CustomerPortalRedux'
import {ChangeTicketSeat, CancelTicket} from '../redux/CustomerPortalCRUD'
import {SwalUtils} from '../../../utils/SwalUtils'
import {TicketSeatReselectionModalFormValues} from '../../../components/forms/TicketSeatReselectionModalForm'
import {BlobUtils} from '../../../utils/BlobUtils'

export const useTicketTableActions = (onRefresh?: () => void, onRefreshCallback?: () => void) => {
  const {pushError, push} = useAlerts()
  const {isLoading, isKeyLoading, setIsLoading} = useLoadingState()
  const dispatch = useDispatch()
  const reassignSeat = useCallback(
    async (values: Required<TicketSeatReselectionModalFormValues>) => {
      const doneLoading = setIsLoading(values.ticketCode)
      try {
        const {data} = await ChangeTicketSeat(values.ticketCode, {
          locationCode: values.locationCode,
          reason: values.reason,
          seatNo: values.column,
          seatRow: values.row,
        })
        BlobUtils.downloadBlob(data, `${Date.now()}.pdf`)
        dispatch(actions.bookingProducts.search())
      } catch (e) {
        pushError(e)
      } finally {
        onRefresh && onRefresh()
        onRefreshCallback && onRefreshCallback()
        doneLoading()
      }
    },
    [dispatch, onRefresh, onRefreshCallback, pushError, setIsLoading]
  )

  const cancel = useCallback(
    async (codes: string[]) => {
      await SwalUtils.asyncSpecifyReason(
        async (reason) => {
          const doneLoading = setIsLoading(codes)
          try {
            await CancelTicket(codes, reason)
            push({
              message: `successfully cancelled.`,
              timeout: 5000,
              variant: 'success',
            })
            onRefresh && onRefresh()
            onRefreshCallback && onRefreshCallback()
          } catch (e) {
            pushError(e)
          } finally {
            doneLoading()
          }
        },
        {
          titleText: 'Cancel Ticket',
          text: 'Please specify a reason.',
          confirmButtonText: 'Cancel!',
          icon: 'question',
          cancelButtonText: "Don't Cancel!",
        }
      )
    },
    [onRefresh, onRefreshCallback, push, pushError, setIsLoading]
  )

  const handleCancelBulk = useCallback(
    (data: TicketPortalModel[]) => {
      cancel(data.map((bookings) => bookings.code))
    },
    [cancel]
  )

  const handleCancelSingle = useCallback(
    (data: TicketPortalModel) => {
      cancel([data.code])
    },
    [cancel]
  )

  return useMemo(
    () => ({
      isLoading,
      isKeyLoading,
      handleCancelBulk,
      cancelSingle: handleCancelSingle,
      reassignSeat,
    }),
    [isLoading, isKeyLoading, handleCancelBulk, handleCancelSingle, reassignSeat]
  )
}

export const isTicketCancel = (ticket: TicketPortalModel) => {
  return ticket.status === 'active' || ticket.status === 'sent'
}

export const isTicketIsReassignable = (ticket: TicketPortalModel) => {
  return ticket.status === 'active' && ticket.isSeated
}
